import React, { memo, ReactNode, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { hideCrisp, showCrisp } from '@app/analytics';

const useStyles = createUseStyles((theme: Theme) => ({
  selectionFooter: {
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
    transition: 'transform .2s',
    transform: 'translateY(0)',

    position: 'sticky',
    zIndex: 1,
    fallbacks: {
      position: 'fixed',
    },
    [theme.media.mobile]: {
      position: 'fixed',
    },
  },

  selectionFooterClosed: {
    position: 'fixed',
    zIndex: 0,
    transform: 'translateY(300px)',
  },
  absolute: {
    position: 'absolute',
  },
  selectionFooterContent: {
    margin: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',

    paddingTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
    paddingLeft: theme.marginBase * 8,
    paddingRight: theme.marginBase * 7,
    '& > *': {
      marginRight: theme.marginBase,
    },
    [theme.media.mobile]: {
      paddingLeft: theme.marginBase,
      paddingRight: 0,
    },
  },
  count: {
    ...theme.fonts.h5,
    flex: 1,
  },
}));

interface Props {
  selectedCount: number;
  children?: React.ReactNode;
  className?: string;
  takeSpace?: boolean;
  text: ReactNode;
}

export const SelectionFooter = memo(
  ({ selectedCount, text, className, children, takeSpace }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme });

    const [ref, setRef] = useState<HTMLDivElement | null>(null);
    const isOpen = selectedCount > 0;
    useEffect(() => {
      if (isOpen) {
        hideCrisp();
        return () => showCrisp();
      } else {
        showCrisp();
        return;
      }
    }, [isOpen]);

    return (
      <>
        {takeSpace && <div style={{ height: ref?.offsetHeight }} />}
        <div
          className={classnames(
            classes.selectionFooter,
            {
              [classes.selectionFooterClosed]: !isOpen,
              [classes.absolute]: isOpen && takeSpace,
            },
            className,
          )}
          ref={setRef}
        >
          <div className={classes.selectionFooterContent}>
            <div className={classes.count}>{text}</div>
            {children}
          </div>
        </div>
      </>
    );
  },
);
